import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Box,
  Typography,
  Popover,
  Collapse,
  Divider,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { Edit, Delete as DeleteIcon } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useLocation } from "react-router-dom";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  getAllItems,
  deleteItem,
  getCategories,
  getSizes,
  ItemListExport,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import { Description as FileIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

export default function ItemTable() {
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [filters, setFilters] = useState({
    name: "",
    category: null,
    size: null,
  });

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ── Solid colors (no alpha) for sticky cells ──
  const tableHeadBg = isDark ? "#252b38" : "#f0f4ff";
  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e"
        : "#1a1d26"
      : index % 2 === 0
        ? "#fafafa"
        : "#ffffff";
  const rowHoverBg = isDark ? "#2c3242" : "#e3f2fd";

  // ── Toast on navigate back ──
  useEffect(() => {
    if (location.state?.action === "edited") {
      toast.success("Item updated successfully!");
      window.history.replaceState({}, document.title);
    } else if (location.state?.action === "created") {
      toast.success("Item created successfully!");
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // ── Fetch items on filter change ──
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, false);
    }, 1000);
    return () => clearTimeout(handler);
  }, [filters]);

  // ── Fetch categories + sizes on mount ──
  useEffect(() => {
    fetchCategoriesData();
    fetchSizesData();
  }, []);

  // ── Scroll lock on desktop ──
  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ── Helpers ──
  const isAnyFilterApplied = () =>
    filters.category !== null ||
    filters.size !== null ||
    filters.name.trim() !== "";

  const fetchItems = async (pageNo = 1, append = false) => {
    const data = await getAllItems(pageNo, {
      name: filters.name,
      category: filters.category?.name || "",
      item_size: filters.size?.id || "",
      date_range: isAnyFilterApplied() ? "all" : "today",
    });
    if (append) {
      setItems((prev) => [...prev, ...data.results]);
    } else {
      setItems(data.results);
    }
    setHasMore(!!data.next);
  };

  const fetchMoreData = () => {
    if (hasMore) {
      fetchItems(page + 1, true);
      setPage((prev) => prev + 1);
    }
  };

  const fetchCategoriesData = async (search = "") => {
    try {
      const data = await getCategories(1, search);
      setCategories(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {}
  };

  const fetchSizesData = async (search = "") => {
    try {
      const data = await getSizes(search);
      setSizes(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {}
  };

  const handleCategorySearch = debounce((val) => fetchCategoriesData(val), 500);
  const handleSizeSearch = debounce((val) => fetchSizesData(val), 500);

  const handleDelete = async (id) => {
    try {
      await deleteItem(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      toast.success("Deleted successfully");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const blob = await ItemListExport({
        name: filters.name || "",
        category_name: filters.category?.name || "",
        item_size: filters.size?.size || "",
        date_range: isAnyFilterApplied() ? "all" : "today",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Item_List_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully!");
    } catch {
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const formatOnlyDate = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    const [year, month, day] = dateTimeString.split("T")[0].split("-");
    return `${day}-${month}-${year}`;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getAvailableStockColor = (item) => {
    const avail =
      parseFloat(item.current_stock || 0) - parseFloat(item.reserve_stock || 0);
    if (avail <= 0) return "error.main";
    if (avail <= parseFloat(item.min_quantity || 0)) return "warning.main";
    return "success.main";
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Filter + Action Bar ── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{
          mt: { xs: 3, md: 1 },
          mb: 1,
          width: "100%",
          gap: 2,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        {/* Category Filter */}
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={filters.category}
          onChange={(e, val) => setFilters({ ...filters, category: val })}
          onInputChange={(e, val, reason) => {
            if (reason === "input") handleCategorySearch(val);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              size="small"
              sx={{
                width: { xs: "100%", sm: "225px" },
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          )}
        />

        {/* Product Type Filter */}
        <Autocomplete
          options={sizes}
          getOptionLabel={(option) => option?.size || ""}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={filters.size}
          onChange={(e, val) => setFilters({ ...filters, size: val })}
          onInputChange={(e, val, reason) => {
            if (reason === "input") handleSizeSearch(val);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product Type"
              size="small"
              sx={{
                width: { xs: "100%", sm: "225px" },
                "& .MuiInputBase-root": {
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          )}
        />

        {/* Item Name Filter */}
        <TextField
          label="Item Name"
          value={filters.name}
          onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          size="small"
          sx={{
            width: { xs: "100%", sm: "225px" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: 0,
            },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />

        {/* Buttons */}
        <Box
          display="flex"
          gap={2}
          ml={{ xs: 0, sm: "auto" }}
          width={{ xs: "100%", sm: "auto" }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() =>
              navigate("/dashboard/item-form", { state: { mode: "create" } })
            }
            sx={{ height: 30, fontSize: "0.7rem", px: 2, borderRadius: 0 }}
          >
            Item
          </Button>

          <Tooltip
            title={excelLoading ? "Exporting..." : "Download Excel"}
            arrow
          >
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading}
                sx={{
                  borderRadius: "4px",
                  padding: "6px",
                  border: "1px solid",
                  borderColor: "success.main",
                  "&:hover": {
                    bgcolor: isDark ? alpha("#4caf50", 0.15) : "#e8f5e9",
                  },
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Infinite Scroll ── */}
      <InfiniteScroll
        dataLength={items.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          <Typography
            sx={{ textAlign: "center", py: 2, color: "text.secondary" }}
          >
            Loading...
          </Typography>
        }
        endMessage={
          <Typography
            sx={{ textAlign: "center", py: 2, color: "text.disabled" }}
          >
            No more items!
          </Typography>
        }
        scrollableTarget={!isMobile ? "desktop-table-container" : undefined}
      >
        {isMobile ? (
          /* ── MOBILE CARDS ── */
          <Box sx={{ bgcolor: "background.paper" }}>
            {items.map((item, index) => (
              <Box key={item.id || index}>
                <Box
                  onClick={() =>
                    setExpandedItem(expandedItem === item.id ? null : item.id)
                  }
                  sx={{
                    p: 1.2,
                    cursor: "pointer",
                    bgcolor: rowBg(index),
                    borderLeft: "4px solid",
                    borderLeftColor:
                      parseFloat(item.current_stock || 0) -
                        parseFloat(item.reserve_stock || 0) <=
                      0
                        ? "error.main"
                        : parseFloat(item.current_stock || 0) -
                              parseFloat(item.reserve_stock || 0) <=
                            parseFloat(item.min_quantity || 0)
                          ? "warning.main"
                          : "primary.main",
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  {/* Row 1: S.No + Item Name + Expand Icon */}
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "text.disabled",
                        minWidth: 18,
                        pt: 0.2,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "text.primary",
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.name || "N/A"}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ p: 0.3, flexShrink: 0 }}>
                      {expandedItem === item.id ? (
                        <ExpandLess sx={{ fontSize: "1rem" }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </Box>

                  {/* Row 2: Category + Product Type */}
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5, ml: 2.8 }}>
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        color: "primary.main",
                        fontWeight: 500,
                      }}
                    >
                      {item.category_name || "—"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.62rem", color: "text.disabled" }}
                    >
                      •
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.62rem", color: "text.secondary" }}
                    >
                      {item.size_name || "—"}
                    </Typography>
                  </Box>

                  {/* Row 3: Stock Info - 3 Columns */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.8,
                      ml: 2.8,
                      mr: 1,
                    }}
                  >
                    {/* Current Stock */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Stock
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {item.current_stock || 0}
                      </Typography>
                    </Box>

                    {/* Available Stock */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Available
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: getAvailableStockColor(item),
                        }}
                      >
                        {Math.max(
                          0,
                          parseFloat(item.current_stock || 0) -
                            parseFloat(item.reserve_stock || 0),
                        ).toFixed(0)}
                      </Typography>
                    </Box>

                    {/* Price */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Price
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {formatPrice(item.price)}
                      </Typography>
                    </Box>

                    {/* Total Value */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Value
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          color: "success.main",
                        }}
                      >
                        {formatPrice(
                          parseFloat(item.price || 0) *
                            parseInt(item.current_stock || 0),
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* ── Expanded Details - Remaining Info ── */}
                <Collapse
                  in={expandedItem === item.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.common.white, 0.05)
                          : alpha(t.palette.primary.main, 0.04),
                      borderLeft: "4px solid",
                      borderLeftColor: "primary.main",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      {[
                        {
                          label: "RESERVE STOCK",
                          value: item.reserve_stock || 0,
                        },
                        { label: "MIN QTY", value: item.min_quantity || 0 },
                        { label: "MAX QTY", value: item.max_quantity || 0 },
                        { label: "UNIT", value: item.unit_name || "N/A" },
                        {
                          label: "CREATED BY",
                          value: item.created_by_name || "N/A",
                        },
                        {
                          label: "CREATED AT",
                          value: formatOnlyDate(item.created_at),
                        },
                      ].map(({ label, value }) => (
                        <Box key={label}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        ACTIONS
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/dashboard/item-form", {
                              state: { mode: "edit", data: item },
                            });
                          }}
                          sx={{
                            bgcolor: isDark
                              ? alpha(theme.palette.primary.main, 0.15)
                              : "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: isDark
                                ? alpha(theme.palette.primary.main, 0.28)
                                : "#bbdefb",
                            },
                          }}
                        >
                          <Edit
                            sx={{ fontSize: "1rem", color: "primary.main" }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setItemToDelete(item.id);
                          }}
                          sx={{
                            bgcolor: isDark
                              ? alpha(theme.palette.error.main, 0.15)
                              : "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: isDark
                                ? alpha(theme.palette.error.main, 0.28)
                                : "#ffcdd2",
                            },
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "1rem", color: "error.main" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
                <Divider />
              </Box>
            ))}
          </Box>
        ) : (
          /* ── DESKTOP TABLE ── */
          <TableContainer
            component={Paper}
            id="desktop-table-container"
            elevation={0}
            sx={{
              maxHeight: "88vh",
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0,
              boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1350,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    color: "text.primary",
                    backgroundColor: tableHeadBg,
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    letterSpacing: "0.04em",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: tableHeadBg,
                      zIndex: 3,
                      minWidth: 70,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 70,
                      backgroundColor: tableHeadBg,
                      zIndex: 3,
                      minWidth: 250,
                    }}
                  >
                    Item Name
                  </TableCell>
                  <TableCell sx={{ backgroundColor: tableHeadBg }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ backgroundColor: tableHeadBg }}>
                    Product Type
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Current Stock
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Reserve Stock
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: tableHeadBg,
                      color: "success.main",
                      fontWeight: "bold",
                    }}
                  >
                    Available Stock
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Min Qty
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Max Qty
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Price
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Total Value
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Created by
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: tableHeadBg }}
                  >
                    Created at
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: tableHeadBg,
                      zIndex: 3,
                      minWidth: 80,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((item, index) => (
                  <TableRow
                    key={item.id || index}
                    sx={{
                      backgroundColor: rowBg(index),
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: `${rowHoverBg} !important`,
                        "& td": { backgroundColor: "inherit !important" },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: "text.primary",
                      },
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: "inherit",
                        zIndex: 2,
                        minWidth: 70,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 70,
                        bgcolor: "inherit",
                        zIndex: 2,
                        minWidth: 230,
                        textAlign: "center !important",
                      }}
                    >
                      {item.name.match(/.{1,70}(\s|$)/g)?.map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </TableCell>
                    <TableCell>{item.category_name}</TableCell>
                    <TableCell>{item.size_name}</TableCell>
                    <TableCell align="right">{item.current_stock}</TableCell>
                    <TableCell align="right">
                      {item.reserve_stock || 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: getAvailableStockColor(item),
                        fontWeight: 600,
                      }}
                    >
                      {Math.max(
                        0,
                        parseFloat(item.current_stock || 0) -
                          parseFloat(item.reserve_stock || 0),
                      ).toFixed(0)}
                    </TableCell>
                    <TableCell align="right">{item.min_quantity}</TableCell>
                    <TableCell align="right">{item.max_quantity}</TableCell>
                    <TableCell align="right">
                      {formatPrice(item.price)}
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(
                        parseFloat(item.price || 0) *
                          parseInt(item.current_stock || 0),
                      )}
                    </TableCell>
                    <TableCell align="right">{item.created_by_name}</TableCell>
                    <TableCell align="right">
                      {formatOnlyDate(item.created_at)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: "inherit",
                        zIndex: 2,
                        minWidth: 80,
                      }}
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        sx={{ padding: 0, px: 1 }}
                        onClick={() =>
                          navigate("/dashboard/item-form", {
                            state: { mode: "edit", data: item },
                          })
                        }
                      >
                        <Edit transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ padding: 0, px: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setItemToDelete(item.id);
                        }}
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setItemToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableScrollLock
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
            Are you sure you want to delete?
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={1} gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                await handleDelete(itemToDelete);
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
